/**
 * Helper: Algolia Insights
 * -----------------------------------------------------------------------------
 * Wrapper around search-insights for click and conversion analytics.
 *
 */
import aa from 'search-insights'

let initialised = false

// Synthetic queryIDs set by recommendations are not valid Algolia search IDs.
function isRecommendationQueryID(queryID) {
  return typeof queryID === 'string' && queryID.startsWith('recommendation_')
}

// Strip accidental "queryID:" prefix that can occur when queryID values
// pass through serialisation layers (e.g. Algolia web-pixel round-trip
// via cart line-item properties).
export function sanitiseQueryID(queryID) {
  if (typeof queryID === 'string' && queryID.startsWith('queryID:')) {
    return queryID.slice('queryID:'.length)
  }
  return queryID
}

/**
 * Initialise search-insights with Algolia credentials.
 * - Sets authenticated user token when a customer is logged in.
 * - Stores config in localStorage for Algolia app's web pixel.
 */
export function initInsights() {
  if (initialised) {
    return
  }

  const credentials = window.cnvs?.algolia?.credentials

  if (!credentials?.appId || !credentials?.apiKey) {
    return
  }

  const customer = window.cnvs?.customer

  aa('init', {
    appId: credentials.appId,
    apiKey: credentials.apiKey,
    useCookie: true,
    ...customer?.loggedIn && customer?.id
      ? { userToken: String(customer.id), authenticatedUserToken: String(customer.id) }
      : {}
    ,
  })

  initialised = true
}

/**
 * Send a click event after search.
 * - Tracks product card clicks from search/browse results.
 * @param {Object} params - Event parameters.
 * @param {String} params.indexName - Algolia index name.
 * @param {String|Number} params.objectID - Algolia object ID.
 * @param {Number} params.position - Hit position in results.
 * @param {String} params.queryID - Algolia query ID.
 */
export function sendClickEvent({ indexName, objectID, position, queryID }) {
  if (!initialised || !queryID) {
    return
  }

  aa('clickedObjectIDsAfterSearch', {
    eventName: 'Product Clicked',
    index: indexName,
    objectIDs: [String(objectID)],
    positions: [Number(position)],
    queryID: sanitiseQueryID(queryID),
  })
}

/**
 * Send an add-to-cart conversion event after search.
 * - Tracks add-to-cart actions from search/browse results.
 * @param {Object} params - Event parameters.
 * @param {String} params.indexName - Algolia index name.
 * @param {String|Number} params.objectID - Algolia object ID.
 * @param {String} params.queryID - Algolia query ID.
 */
export function sendConversionEvent({ indexName, objectID, queryID }) {
  if (!initialised || !queryID) {
    return
  }

  // Recommendations carry synthetic queryID
  if (isRecommendationQueryID(queryID)) {
    aa('addedToCartObjectIDs', {
      eventName: 'Product Added to Cart',
      index: indexName,
      objectIDs: [String(objectID)],
    })
    return
  }

  aa('addedToCartObjectIDsAfterSearch', {
    eventName: 'Product Added to Cart',
    index: indexName,
    objectIDs: [String(objectID)],
    queryID: sanitiseQueryID(queryID),
  })
}

/**
 * Send a purchase conversion event after search.
 * - Tracks completed purchases for items originating from search/browse.
 * @param {Object} params - Event parameters.
 * @param {String} params.indexName - Algolia index name.
 * @param {Array} params.objectIDs - Array of Algolia object IDs.
 * @param {String} params.queryID - Algolia query ID.
 */
export function sendPurchaseEvent({ indexName, objectIDs, queryID }) {
  if (!initialised || !queryID) {
    return
  }

  aa('purchasedObjectIDsAfterSearch', {
    eventName: 'Products Purchased',
    index: indexName,
    objectIDs: objectIDs.map(String),
    queryID: sanitiseQueryID(queryID),
  })
}

/**
 * Send a view event for products displayed from search/browse results.
 * - Tracks when products from Algolia results are viewed on a detail page.
 * @param {Object} params - Event parameters.
 * @param {String} params.indexName - Algolia index name.
 * @param {Array} params.objectIDs - Array of Algolia object IDs.
 */
export function sendViewEvent({ indexName, objectIDs }) {
  if (!initialised) {
    return
  }

  aa('viewedObjectIDs', {
    eventName: 'Products Viewed',
    index: indexName,
    objectIDs: objectIDs.map(String),
  })
}

/**
 * Send a custom event when the search bar is clicked/focused.
 * - Tracks when users interact with the search bar.
 * @param {Object} params - Event parameters.
 * @param {String} params.indexName - Algolia index name.
 */
export function sendSearchBarClickEvent({ indexName }) {
  if (!initialised) {
    return
  }

  aa('viewedFilters', {
    eventName: 'Search Bar Clicked',
    index: indexName,
    filters: ['action:search_bar_clicked'],
  })
}

/**
 * Send a custom event when the user types a query in the search bar.
 * - Tracks search queries entered by users.
 * @param {Object} params - Event parameters.
 * @param {String} params.indexName - Algolia index name.
 * @param {String} params.query - The search query typed by the user.
 */
export function sendSearchQueryEvent({ indexName, query }) {
  if (!initialised || !query) {
    return
  }

  aa('viewedFilters', {
    eventName: 'Search Query Submitted',
    index: indexName,
    filters: [`query:${query}`],
  })
}
